import React, { useEffect, useRef, useState } from "react";

// Width-aware SVG wrapper so axis text stays at real pixel sizes (≥11px).
export function useWidth(initial = 600) {
  const ref = useRef(null);
  const [w, setW] = useState(initial);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.max(200, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

const niceUsd = (v) => {
  const a = Math.abs(v);
  const body = a >= 1000 ? `${(a / 1000).toFixed(a >= 10000 ? 0 : 1)}k` : a.toFixed(0);
  return `${v < 0 ? "-" : v > 0 ? "+" : ""}$${body}`;
};

// Time-scaled line (x = real time), zero baseline, min/mid/max axis — the
// axis is computed from exactly the points drawn.
export function LineChart({ points, height = 240, fmtX, color = "#d9ff57", ariaLabel }) {
  const [ref, W] = useWidth();
  const H = height;
  const pad = { t: 10, r: 12, b: 24, l: 56 };
  if (!points || points.length < 2) {
    return <div ref={ref} className="mk-chart-empty" style={{ height }}>Not enough closed trades in this window to draw a curve.</div>;
  }
  const xs = points.map((p) => p.t);
  const ys = points.map((p) => p.y).concat(0);
  const x0 = Math.min(...xs), x1 = Math.max(...xs);
  const y0 = Math.min(...ys), y1 = Math.max(...ys);
  const yspan = y1 - y0 || 1;
  const xspan = x1 - x0 || 1;
  const X = (t) => pad.l + ((t - x0) / xspan) * (W - pad.l - pad.r);
  const Y = (v) => pad.t + (1 - (v - y0) / yspan) * (H - pad.t - pad.b);
  const d = points.map((p, i) => `${i ? "L" : "M"}${X(p.t).toFixed(1)},${Y(p.y).toFixed(1)}`).join(" ");
  const area = `${d} L${X(x1).toFixed(1)},${Y(0).toFixed(1)} L${X(x0).toFixed(1)},${Y(0).toFixed(1)} Z`;
  const ticks = [y1, (y0 + y1) / 2, y0];
  const xt = [x0, x0 + xspan / 2, x1];
  const last = points[points.length - 1];
  return (
    <div ref={ref} className="mk-chart">
      <svg width={W} height={H} role="img" aria-label={ariaLabel}>
        {ticks.map((v, i) => (
          <g key={i}>
            <line x1={pad.l} x2={W - pad.r} y1={Y(v)} y2={Y(v)} className="mk-grid-line" />
            <text x={pad.l - 6} y={Y(v) + 4} textAnchor="end" className="mk-axis">{niceUsd(v)}</text>
          </g>
        ))}
        <line x1={pad.l} x2={W - pad.r} y1={Y(0)} y2={Y(0)} className="mk-zero-line" />
        <path d={area} fill={color} opacity="0.08" />
        <path d={d} fill="none" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
        <circle cx={X(last.t)} cy={Y(last.y)} r="3.5" fill={color} />
        {xt.map((t, i) => (
          <text key={i} x={X(t)} y={H - 6} textAnchor={i === 0 ? "start" : i === 2 ? "end" : "middle"} className="mk-axis">{fmtX(t)}</text>
        ))}
      </svg>
    </div>
  );
}

export function BarChart({ buckets, height = 240, fmtX, ariaLabel }) {
  const [ref, W] = useWidth();
  const H = height;
  const pad = { t: 10, r: 12, b: 24, l: 56 };
  if (!buckets || !buckets.length) {
    return <div ref={ref} className="mk-chart-empty" style={{ height }}>No closed trades in this window.</div>;
  }
  const vals = buckets.map((b) => b.pnl).concat(0);
  const y0 = Math.min(...vals), y1 = Math.max(...vals);
  const span = y1 - y0 || 1;
  const Y = (v) => pad.t + (1 - (v - y0) / span) * (H - pad.t - pad.b);
  const bw = (W - pad.l - pad.r) / buckets.length;
  const xt = [0, Math.floor(buckets.length / 2), buckets.length - 1];
  return (
    <div ref={ref} className="mk-chart">
      <svg width={W} height={H} role="img" aria-label={ariaLabel}>
        {[y1, 0, y0].map((v, i) => (
          <g key={i}>
            <line x1={pad.l} x2={W - pad.r} y1={Y(v)} y2={Y(v)} className={v === 0 ? "mk-zero-line" : "mk-grid-line"} />
            <text x={pad.l - 6} y={Y(v) + 4} textAnchor="end" className="mk-axis">{niceUsd(v)}</text>
          </g>
        ))}
        {buckets.map((b, i) => {
          const top = Math.min(Y(b.pnl), Y(0));
          const h = Math.max(1, Math.abs(Y(b.pnl) - Y(0)));
          return <rect key={i} x={pad.l + i * bw + 1} y={top} width={Math.max(1, bw - 2)} height={h}
            className={b.pnl >= 0 ? "mk-bar-up" : "mk-bar-down"}><title>{`${fmtX(b.t)}: ${niceUsd(b.pnl)}`}</title></rect>;
        })}
        {xt.map((i, k) => (
          <text key={k} x={pad.l + i * bw + bw / 2} y={H - 6} textAnchor={k === 0 ? "start" : k === 2 ? "end" : "middle"} className="mk-axis">{fmtX(buckets[i].t)}</text>
        ))}
      </svg>
    </div>
  );
}
